import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { GetTransactionsRequest } from "@/types/requests";
import { Transaction } from "@/types/resources";
import { GetBalanceResponse, GetTransactionsResponse } from "@/types/responses";

const useGetBalance = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const fetch = useCallback(() => {
    client
      .get<GetBalanceResponse>("/balance")
      .then((response) => {
        setBalance(response.data.balance);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { balance, error, loading, refetch };
};

const useGetTransactions = (params: GetTransactionsRequest) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const fetch = useCallback(() => {
    client
      .get<GetTransactionsResponse>("/transactions", { params })
      .then((response) => {
        setTransactions(response.data.transactions);
        setCount(response.data.count);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    params.page,
    params.type,
    params.items_per_page,
    params.start_date,
    params.end_date,
  ]);

  // Re-enter the loading state synchronously during render (not in an effect)
  // whenever the query params change, so the UI shows loading on refetch.
  const paramsKey = JSON.stringify([
    params.page,
    params.type,
    params.items_per_page,
    params.start_date,
    params.end_date,
  ]);
  const [prevParamsKey, setPrevParamsKey] = useState(paramsKey);
  if (prevParamsKey !== paramsKey) {
    setPrevParamsKey(paramsKey);
    setLoading(true);
    setError(null);
  }

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { count, error, loading, refetch, transactions };
};

export { useGetBalance, useGetTransactions };
